import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import type { AppSetting } from '../api/types'

export function useSettings() {
  // /settings requires Perm.SETTINGS_READ (admin + maintainer only). Firing
  // this unconditionally for every logged-in user surfaces a red 403 toast
  // on radiologist / technician / developer / qa accounts. Gate the query
  // so those roles fall through to caller-side defaults (see useReworkLimit
  // below) rather than raising a permission error they can't act on.
  const { can } = useAuth()
  return useQuery<AppSetting[]>({
    queryKey: ['settings'],
    queryFn: () => apiClient.getSettings(),
    staleTime: 60_000, // settings change rarely
    enabled: can('settings:read'),
  })
}

export function useSetting(key: string): unknown {
  const { data } = useSettings()
  return data?.find(s => s.key === key)?.value
}

/**
 * Disapprovals before a mask escalates to a maintainer. Falls back to 2, which
 * is both the seeded value and the worker's own fallback — so a card rendered
 * before settings load shows the same number the workflow will use.
 */
export function useReworkLimit(): number {
  const raw = useSetting('segmentation.rework_limit')
  return typeof raw === 'number' ? raw : 2
}
